import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { PageHeader, EmptyState, Modal, ScrollTable, Pagination, PAGE_SIZE, SearchSelect } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// 採購單品項為自由文字（雜項支出 / 設備採購），不連動庫存

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_STEPS = ['draft', 'pending', 'approved', 'ordered', 'received']

const emptySupplier = () => ({ name: '', code: '', contact: '', phone: '', email: '', address: '', payment_terms: '', note: '', is_active: true })
const emptyPO       = () => ({ po_no: '', supplier_id: '', supplier_name: '', status: 'draft', requested_by: '', expected_date: '', note: '' })
const emptyItem     = () => ({ item_name: '', qty: '', unit_price: '', unit: '式', total_price: 0 })

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = n => (n ?? 0).toLocaleString()
const isOverdue = o => o.expected_date && o.status !== 'received' && o.status !== 'cancelled' && new Date(o.expected_date) < new Date()

function exportCSV(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

// ── Status pipeline ───────────────────────────────────────────────────────────
function StatusPipeline({ status, PO_STATUS }) {
  if (status === 'cancelled') {
    const si = PO_STATUS.find(s => s.key === 'cancelled') || {}
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', background: '#fee2e2', padding: '3px 10px', borderRadius: 20, border: '1px solid #fca5a5' }}>
          ✕ {si.label}
        </span>
      </div>
    )
  }
  const currentIdx = STATUS_STEPS.indexOf(status)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STATUS_STEPS.map((key, i) => {
        const s      = PO_STATUS.find(x => x.key === key) || PO_STATUS[0]
        const done   = i < currentIdx
        const active = i === currentIdx
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '4px 7px', borderRadius: 8,
              background: active ? s.color : done ? '#e0f2fe' : '#f1f5f9',
              border: `1.5px solid ${active ? s.color : done ? '#93c5fd' : C.border}`,
              transition: 'all 0.2s',
            }}>
              <span style={{ fontSize: 11 }}>{done ? '✓' : s.icon}</span>
              <span style={{ fontSize: 9, fontWeight: active ? 800 : 500, color: active ? '#fff' : done ? '#3b82f6' : C.muted, whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div style={{ width: 14, height: 2, background: i < currentIdx ? '#93c5fd' : C.border }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── PO item form ──────────────────────────────────────────────────────────────
function POFormBody({ poForm, setPoForm, poItems, setPoItems, poTotal, activeSupplierOptions, suppliers, updateItem, _ }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <label style={st.label}>{_('purPoNoLabel')}</label>
          <input style={st.input} value={poForm.po_no} placeholder={_('purPoNoPh')} onChange={e => setPoForm(f => ({ ...f, po_no: e.target.value }))} />
        </div>
        <div>
          <label style={st.label}>{_('purSupplierLabel')}</label>
          <SearchSelect
            options={activeSupplierOptions}
            value={poForm.supplier_id}
            onChange={val => {
              const s = suppliers.find(s => s.id === val)
              setPoForm(f => ({ ...f, supplier_id: val, supplier_name: s?.name || '' }))
            }}
            placeholder={_('purSearchSupplier')}
          />
        </div>
        <div>
          <label style={st.label}>{_('purRequester')}</label>
          <input style={st.input} value={poForm.requested_by} onChange={e => setPoForm(f => ({ ...f, requested_by: e.target.value }))} />
        </div>
        <div>
          <label style={st.label}>{_('purExpectedDate')}</label>
          <input type="date" style={st.input} value={poForm.expected_date} onChange={e => setPoForm(f => ({ ...f, expected_date: e.target.value }))} />
        </div>
      </div>
      <div>
        <label style={st.label}>{_('purNoteLabel')}</label>
        <input style={st.input} value={poForm.note} placeholder="採購原因、特殊要求..." onChange={e => setPoForm(f => ({ ...f, note: e.target.value }))} />
      </div>

      {/* Items */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>{_('purItems')}</div>
        {poItems.map((item, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 0.7fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'flex-end' }}>
            <div>
              {i === 0 && <label style={st.label}>{_('purItemName')}</label>}
              <input style={st.input} value={item.item_name} placeholder="例：辦公桌椅、電腦設備、清潔用品..."
                onChange={e => updateItem(i, 'item_name', e.target.value)} />
            </div>
            <div>
              {i === 0 && <label style={st.label}>{_('purQty')}</label>}
              <input type="number" min="0" style={st.input} value={item.qty} placeholder="0"
                onChange={e => updateItem(i, 'qty', e.target.value)} />
            </div>
            <div>
              {i === 0 && <label style={st.label}>{_('purUnit')}</label>}
              <input style={st.input} value={item.unit} placeholder="式/個/組"
                onChange={e => updateItem(i, 'unit', e.target.value)} />
            </div>
            <div>
              {i === 0 && <label style={st.label}>{_('purUnitPrice')}</label>}
              <input type="number" min="0" style={st.input} value={item.unit_price} placeholder="0"
                onChange={e => updateItem(i, 'unit_price', e.target.value)} />
            </div>
            <div style={{ paddingBottom: 2 }}>
              {poItems.length > 1 && (
                <button style={{ ...st.btn('danger'), padding: '13px 12px', minHeight: 48 }}
                  onClick={() => setPoItems(poItems.filter((_, j) => j !== i))}>✕</button>
              )}
            </div>
          </div>
        ))}
        <button style={{ ...st.btn('default'), padding: '8px 16px', marginTop: 4 }}
          onClick={() => setPoItems([...poItems, emptyItem()])}>{_('purAddItem')}</button>
      </div>

      {/* Total */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', background: C.accentDim, borderRadius: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.accent }}>
          總金額 Total：NT$ {poTotal.toLocaleString()}
        </div>
      </div>
    </div>
  )
}

// ── PO Card (card view) ───────────────────────────────────────────────────────
function POCard({ o, canApprove, canManage, onDetail, onEdit, onStatus, onCancel, onDelete, PO_STATUS, _ }) {
  const si       = PO_STATUS.find(s => s.key === o.status) || PO_STATUS[0]
  const overdue  = isOverdue(o)
  const daysDiff = o.expected_date
    ? Math.floor((new Date(o.expected_date) - new Date()) / 86400000)
    : null

  return (
    <div style={{
      background: '#fff', borderRadius: 14, border: `2px solid ${overdue ? '#fed7aa' : si.color + '55'}`,
      padding: '14px 16px', marginBottom: 10,
      background: overdue ? '#fffbf0' : '#fff',
    }}>
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: C.accent, fontFamily: 'monospace' }}>{o.po_no}</span>
            {overdue && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5' }}>
                ⏰ 逾期到貨
              </span>
            )}
            {!overdue && daysDiff !== null && daysDiff >= 0 && daysDiff <= 3 && o.status !== 'received' && o.status !== 'cancelled' && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#fff7ed', color: '#f97316' }}>
                📦 {daysDiff === 0 ? '今日到貨' : `${daysDiff} 天後到貨`}
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 3 }}>{o.supplier_name}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: C.muted }}>
            {o.requested_by && <span>👤 {o.requested_by}</span>}
            {o.expected_date && <span>📅 預計 {formatDate(o.expected_date)}</span>}
            {o.approved_by && <span>✅ 核准：{o.approved_by}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: C.accent }}>NT$ {fmt(o.total_amount)}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{formatDate(o.created_at)}</div>
        </div>
      </div>

      {/* Status pipeline */}
      <div style={{ marginBottom: 12, overflowX: 'auto' }}>
        <StatusPipeline status={o.status} PO_STATUS={PO_STATUS} />
      </div>

      {o.note && (
        <div style={{ fontSize: 11, color: C.sub, background: '#f8fafc', borderRadius: 8, padding: '6px 10px', marginBottom: 10, borderLeft: `3px solid ${C.border}` }}>
          📝 {o.note}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={{ ...st.btn('default'), padding: '6px 12px', fontSize: 12 }} onClick={() => onDetail(o)}>
          {_('purBtnDetail')}
        </button>
        {(o.status === 'draft' || o.status === 'pending') && (
          <button style={{ ...st.btn('warning'), padding: '6px 12px', fontSize: 12 }} onClick={() => onEdit(o)}>
            ✏️ 編輯
          </button>
        )}
        {o.status === 'draft' && (
          <button style={{ ...st.btn('primary'), padding: '6px 12px', fontSize: 12 }} onClick={() => onStatus(o.id, o.po_no, 'pending')}>
            {_('purBtnSubmit')}
          </button>
        )}
        {o.status === 'pending' && canApprove && (
          <button style={{ ...st.btn('success'), padding: '6px 12px', fontSize: 12 }} onClick={() => onStatus(o.id, o.po_no, 'approved')}>
            {_('purBtnApprove')}
          </button>
        )}
        {o.status === 'approved' && (
          <button style={{ ...st.btn('primary'), padding: '6px 12px', fontSize: 12 }} onClick={() => onStatus(o.id, o.po_no, 'ordered')}>
            {_('purBtnOrdered')}
          </button>
        )}
        {o.status === 'ordered' && (
          <button style={{ ...st.btn('success'), padding: '6px 12px', fontSize: 12 }} onClick={() => onStatus(o.id, o.po_no, 'received')}>
            {_('purBtnReceived')}
          </button>
        )}
        {!['received', 'cancelled'].includes(o.status) && canManage && (
          <button style={{ ...st.btn('danger'), padding: '6px 12px', fontSize: 12 }} onClick={() => onCancel(o)}>
            {_('purBtnCancel')}
          </button>
        )}
        {canManage && ['draft', 'pending'].includes(o.status) && (
          <button style={{ ...st.btn('danger'), padding: '6px 12px', fontSize: 12 }} onClick={() => onDelete(o.id, o.po_no)}>
            🗑 刪除
          </button>
        )}
      </div>
    </div>
  )
}

// ── Supplier card ─────────────────────────────────────────────────────────────
function SupplierCard({ s, orders, canManage, onEdit, onToggle, onDelete, onDetail, _ }) {
  const myOrders = orders.filter(o => o.supplier_id === s.id || o.supplier_name === s.name)
  const totalSpent = myOrders.filter(o => o.status === 'received').reduce((a, o) => a + (o.total_amount || 0), 0)
  const activeOrders = myOrders.filter(o => !['received', 'cancelled'].includes(o.status)).length

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `2px solid ${s.is_active ? C.border : '#e2e8f0'}`, padding: '14px 16px', opacity: s.is_active ? 1 : 0.7 }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{s.name}</span>
            {s.code && <span style={{ fontSize: 10, fontFamily: 'monospace', color: C.accent, background: C.accentDim, padding: '1px 7px', borderRadius: 8 }}>{s.code}</span>}
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: s.is_active ? '#dcfce7' : '#f1f5f9', color: s.is_active ? '#22c55e' : C.muted }}>
              {s.is_active ? _('purSupplierActive') : _('purSupplierInactive')}
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.muted, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {s.contact && <span>👤 {s.contact}</span>}
            {s.phone && <span>📞 {s.phone}</span>}
            {s.payment_terms && <span>💳 {s.payment_terms}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#7c3aed' }}>NT$ {fmt(totalSpent)}</div>
          <div style={{ fontSize: 10, color: C.muted }}>累計採購</div>
        </div>
      </div>

      {/* Mini stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[
          { label: '採購單', value: myOrders.length, color: C.accent },
          { label: '進行中', value: activeOrders, color: activeOrders > 0 ? '#f59e0b' : C.muted },
          { label: '已收貨', value: myOrders.filter(o => o.status === 'received').length, color: '#22c55e' },
        ].map(k => (
          <div key={k.label} style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '6px 8px', textAlign: 'center', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 15, fontWeight: 900, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 9, color: C.muted }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={{ ...st.btn('default'), padding: '6px 12px', fontSize: 12 }} onClick={() => onDetail(s)}>
          🔍 詳情
        </button>
        <button style={{ ...st.btn('warning'), padding: '6px 12px', fontSize: 12 }} onClick={() => onEdit(s)}>
          ✏️ 編輯
        </button>
        <button style={{ ...st.btn(s.is_active ? 'default' : 'success'), padding: '6px 12px', fontSize: 12 }} onClick={() => onToggle(s)}>
          {s.is_active ? _('purSupplierInactive') : '啟用'}
        </button>
        {canManage && (
          <button style={{ ...st.btn('danger'), padding: '6px 12px', fontSize: 12 }} onClick={() => onDelete(s.id, s.name)}>
            🗑
          </button>
        )}
      </div>
    </div>
  )
}

// ── ColBarChart (local, no external dep) ─────────────────────────────────────
function ColBarChart({ data, color = C.accent, height = 120 }) {
  const maxVal = Math.max(...data.map(d => d.value || 0), 1)
  if (!data.length) return <div style={{ color: C.muted, fontSize: 13, padding: 16 }}>尚無資料</div>
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'flex-end' }}>
            {d.value > 0 && <div style={{ fontSize: 8, color, fontWeight: 700, lineHeight: 1 }}>{d.value > 9999 ? `${(d.value/1000).toFixed(0)}k` : fmt(d.value)}</div>}
            <div style={{ width: '100%', background: color, borderRadius: '3px 3px 0 0', height: `${(d.value || 0) / maxVal * 100}%`, minHeight: d.value > 0 ? 3 : 0, opacity: 0.85 }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</div>
        ))}
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Purchasing({ products, userProfile, showToast, addLog, onRefresh, enabledModules = [], isMobile = false }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  const isAdmin    = userProfile?.role === 'admin'
  const pos        = userProfile?.position || 'user'
  const hasPurchasingPerm = enabledModules.includes('purchasing')
  const canApprove = isAdmin || ['chairman', 'ceo', 'vice_ceo', 'factory_manager', 'senior_manager'].includes(pos)
  const canManage  = canApprove || hasPurchasingPerm || ['section_chief', 'team_leader', 'accountant'].includes(pos)

  // PO_STATUS inside component so labels are translated
  const PO_STATUS = [
    { key: 'draft',     label: _('purStatusDraft'),     icon: '📝', color: '#94a3b8', bg: '#f1f5f9' },
    { key: 'pending',   label: _('purStatusPending'),   icon: '⏳', color: '#f59e0b', bg: '#fffbeb' },
    { key: 'approved',  label: _('purStatusApproved'),  icon: '✅', color: C.accent,  bg: C.accentDim },
    { key: 'ordered',   label: _('purStatusOrdered'),   icon: '📦', color: '#7c3aed', bg: '#ede9fe' },
    { key: 'received',  label: _('purStatusReceived'),  icon: '🎉', color: '#22c55e', bg: '#dcfce7' },
    { key: 'cancelled', label: _('purStatusCancelled'), icon: '✕',  color: '#ef4444', bg: '#fee2e2' },
  ]
  const statusInfo = k => PO_STATUS.find(s => s.key === k) || PO_STATUS[0]

  const [activeTab, setActiveTab] = useState('po')
  const [suppliers, setSuppliers] = useState([])
  const [orders,    setOrders]    = useState([])
  const [saving,    setSaving]    = useState(false)
  const [page,      setPage]      = useState(1)
  const [supplierPage, setSupplierPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState('all')
  const [search,     setSearch]    = useState('')
  const [supplierSearch, setSupplierSearch] = useState('')
  const [viewMode,   setViewMode]  = useState(isMobile ? 'card' : 'table')   // 'card' | 'table'; mobile always card

  // Supplier form
  const [showAddSupplier, setShowAddSupplier] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState(null)
  const [supplierForm,    setSupplierForm]    = useState(emptySupplier())
  const [detailSupplier,  setDetailSupplier]  = useState(null)

  // PO form
  const [showAddPO, setShowAddPO]  = useState(false)
  const [editingPO, setEditingPO]  = useState(null)
  const [poForm,    setPoForm]     = useState(emptyPO())
  const [poItems,   setPoItems]    = useState([emptyItem()])
  const [detailPO,  setDetailPO]   = useState(null)
  const [detailItems, setDetailItems] = useState([])

  // Cancel modal
  const [cancelTarget, setCancelTarget] = useState(null)
  const [cancelReason, setCancelReason] = useState('')

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = async () => {
    const cid = userProfile?.company_id
    let supQ = supabase.from('suppliers').select('*')
    if (cid) supQ = supQ.eq('company_id', cid)
    let ordQ = supabase.from('purchase_orders').select('*')
    if (cid) ordQ = ordQ.eq('company_id', cid)
    const [{ data: sup }, { data: ord }] = await Promise.all([
      supQ.order('name').limit(300),
      ordQ.order('created_at', { ascending: false }).limit(500),
    ])
    if (sup) setSuppliers(sup)
    if (ord) setOrders(ord)
  }
  useEffect(() => { load() }, [])

  // Auto-fill PO no
  useEffect(() => {
    if (showAddPO && !editingPO) {
      const d = new Date()
      const prefix = `PO-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`
      supabase.from('purchase_orders').select('id', { count: 'exact', head: true }).like('po_no', `${prefix}%`)
        .then(({ count }) => setPoForm(f => ({ ...f, po_no: `${prefix}-${String((count||0)+1).padStart(3,'0')}`, requested_by: userProfile?.name || '' })))
    }
  }, [showAddPO])

  // ── Computed ──────────────────────────────────────────────────────────────
  const filteredOrders = useMemo(() => orders.filter(o => {
    if (filterStatus !== 'all' && o.status !== filterStatus) return false
    if (search && !o.po_no?.toLowerCase().includes(search.toLowerCase()) && !o.supplier_name?.includes(search)) return false
    return true
  }), [orders, filterStatus, search])

  const pagedOrders = useMemo(() => filteredOrders.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filteredOrders, page])
  const totalOrderPages = useMemo(() => Math.ceil(filteredOrders.length / PAGE_SIZE), [filteredOrders])

  const filteredSuppliers = useMemo(() => suppliers.filter(s =>
    !supplierSearch || s.name.includes(supplierSearch) || (s.code || '').includes(supplierSearch) || (s.contact || '').includes(supplierSearch)
  ), [suppliers, supplierSearch])
  const pagedSuppliers = useMemo(() => filteredSuppliers.slice((supplierPage-1)*PAGE_SIZE, supplierPage*PAGE_SIZE), [filteredSuppliers, supplierPage])
  const totalSupplierPages = useMemo(() => Math.ceil(filteredSuppliers.length / PAGE_SIZE), [filteredSuppliers])

  const activeSupplierOptions = useMemo(() =>
    suppliers.filter(s => s.is_active).map(s => ({ value: s.id, label: s.name, sub: s.code || '' }))
  , [suppliers])

  const poTotal = useMemo(() =>
    poItems.reduce((a, i) => a + (parseFloat(i.qty)||0) * (parseFloat(i.unit_price)||0), 0)
  , [poItems])

  const updateItem = (i, key, val) => {
    const n = [...poItems]
    n[i][key] = val
    if (key === 'qty' || key === 'unit_price') n[i].total_price = (parseFloat(n[i].qty)||0) * (parseFloat(n[i].unit_price)||0)
    setPoItems(n)
  }

  // KPI
  const kpi = useMemo(() => {
    const now = new Date().toISOString().slice(0,7)
    return {
      activeSuppliers: suppliers.filter(s => s.is_active).length,
      totalOrders: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      ordered: orders.filter(o => o.status === 'ordered').length,
      received: orders.filter(o => o.status === 'received').length,
      overdue: orders.filter(isOverdue).length,
      thisMonthSpend: orders.filter(o => o.status === 'received' && (o.created_at||'').slice(0,7) === now).reduce((a,o) => a+( o.total_amount||0), 0),
    }
  }, [suppliers, orders])

  // Analytics
  const analytics = useMemo(() => {
    // Monthly spend + count (last 6 months)
    const months6 = Array.from({length:6}, (_,i) => {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (5-i))
      return d.toISOString().slice(0,7)
    })
    const monthlySpend = months6.map(m => ({
      label: `${parseInt(m.slice(5))}月`,
      value: orders.filter(o => o.status === 'received' && (o.created_at||'').slice(0,7) === m).reduce((a,o) => a+(o.total_amount||0), 0),
    }))
    const monthlyCount = months6.map(m => ({
      label: `${parseInt(m.slice(5))}月`,
      value: orders.filter(o => (o.created_at||'').slice(0,7) === m).length,
    }))

    // Top suppliers by received amount
    const supMap = {}
    orders.filter(o => o.status === 'received').forEach(o => {
      supMap[o.supplier_name] = (supMap[o.supplier_name] || 0) + (o.total_amount || 0)
    })
    const topSuppliers = Object.entries(supMap)
      .map(([label, value]) => ({ label, value }))
      .sort((a,b) => b.value - a.value).slice(0, 6)

    // Status distribution
    const byStatus = PO_STATUS.filter(s => s.key !== 'draft').map(s => ({
      label: s.label, color: s.color, bg: s.bg, icon: s.icon,
      count: orders.filter(o => o.status === s.key).length,
    })).filter(s => s.count > 0)

    return { monthlySpend, monthlyCount, topSuppliers, byStatus }
  }, [orders, PO_STATUS])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSaveSupplier = async () => {
    if (!supplierForm.name) { showToast('請填寫供應商名稱', 'error'); return }
    setSaving(true)
    if (editingSupplier) {
      await supabase.from('suppliers').update({ ...supplierForm, updated_at: new Date().toISOString() }).eq('id', editingSupplier.id)
      showToast('✅ 供應商已更新'); setEditingSupplier(null)
    } else {
      await supabase.from('suppliers').insert({ ...supplierForm, company_id: userProfile?.company_id || null })
      showToast('✅ 供應商已新增'); setShowAddSupplier(false)
    }
    await addLog('供應商管理', `${editingSupplier ? '更新' : '新增'}：${supplierForm.name}`)
    setSaving(false); setSupplierForm(emptySupplier()); load()
  }

  const handleDeleteSupplier = async (id, name) => {
    if (!window.confirm(`確定刪除供應商「${name}」？`)) return
    await supabase.from('suppliers').delete().eq('id', id)
    await addLog('刪除供應商', name); showToast('✅ 已刪除'); load()
  }

  const handleSavePO = async () => {
    if (!poForm.po_no) { showToast('請填寫採購單號', 'error'); return }
    if (!poForm.supplier_id) { showToast('請選擇供應商', 'error'); return }
    const validItems = poItems.filter(i => i.item_name?.trim())
    if (!validItems.length) { showToast('請填寫至少一項採購品項', 'error'); return }
    setSaving(true)
    const payload = { ...poForm, total_amount: poTotal, updated_at: new Date().toISOString() }
    if (editingPO) {
      await supabase.from('purchase_orders').update(payload).eq('id', editingPO.id)
      await supabase.from('purchase_items').delete().eq('po_id', editingPO.id)
      await supabase.from('purchase_items').insert(validItems.map(i => ({ ...i, po_id: editingPO.id, company_id: userProfile?.company_id || null })))
      showToast('✅ 採購單已更新'); setEditingPO(null)
    } else {
      const { data } = await supabase.from('purchase_orders').insert({ ...payload, company_id: userProfile?.company_id || null }).select().single()
      if (data) await supabase.from('purchase_items').insert(validItems.map(i => ({ ...i, po_id: data.id, company_id: userProfile?.company_id || null })))
      showToast('✅ 採購單已建立'); setShowAddPO(false)
    }
    await addLog('採購管理', `${editingPO ? '更新' : '建立'}採購單：${poForm.po_no}`)
    setSaving(false); setPoForm(emptyPO()); setPoItems([emptyItem()]); load()
  }

  const handleUpdateStatus = async (id, po_no, newStatus) => {
    if (newStatus === 'received') {
      showToast(`🎉 採購單 ${po_no} 已確認收貨`)
    } else {
      showToast(`✅ 狀態更新：${statusInfo(newStatus).label}`)
    }
    await supabase.from('purchase_orders').update({ status: newStatus, updated_at: new Date().toISOString(), ...(newStatus === 'approved' ? { approved_by: userProfile?.name } : {}) }).eq('id', id)
    await addLog('採購狀態', `${po_no} → ${statusInfo(newStatus).label}`)
    load()
  }

  const handleCancelConfirm = async () => {
    await supabase.from('purchase_orders').update({
      status: 'cancelled', note: cancelReason ? `[取消原因] ${cancelReason}` : cancelTarget.note,
      updated_at: new Date().toISOString()
    }).eq('id', cancelTarget.id)
    await addLog('取消採購單', `${cancelTarget.po_no}${cancelReason ? `：${cancelReason}` : ''}`)
    showToast('📦 採購單已取消')
    setCancelTarget(null); setCancelReason(''); load()
  }

  const handleDeletePO = async (id, po_no) => {
    if (!window.confirm(`確定刪除採購單 ${po_no}？`)) return
    await supabase.from('purchase_orders').delete().eq('id', id)
    showToast('✅ 已刪除'); load()
  }

  const openDetailPO = async (order) => {
    setDetailPO(order)
    const { data } = await supabase.from('purchase_items').select('*').eq('po_id', order.id)
    setDetailItems(data || [])
  }

  const openEditPO = async (order) => {
    setEditingPO(order)
    setPoForm({ ...order })
    const { data } = await supabase.from('purchase_items').select('*').eq('po_id', order.id)
    // backward compat: old rows may use product_name instead of item_name
    setPoItems(data?.length > 0 ? data.map(i => ({ ...i, item_name: i.item_name || i.product_name || '' })) : [emptyItem()])
    setShowAddPO(true)
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="採購管理" en="Purchasing Management" vi="Mua hàng" />

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { icon: '🏭', label: _('purKpiSuppliers'), value: kpi.activeSuppliers, color: C.accent, bg: C.accentDim },
          { icon: '📋', label: _('purKpiTotalPO'), value: kpi.totalOrders, color: '#7c3aed', bg: '#ede9fe' },
          { icon: '⏳', label: _('purKpiPending'), value: kpi.pending, color: '#f59e0b', bg: '#fffbeb' },
          { icon: '📦', label: _('purKpiOrdered'), value: kpi.ordered, color: '#0891b2', bg: '#e0f2fe' },
          { icon: '✅', label: _('purKpiReceived'), value: kpi.received, color: '#22c55e', bg: '#dcfce7' },
          { icon: '⏰', label: _('purKpiOverdue'), value: kpi.overdue, color: kpi.overdue > 0 ? '#ef4444' : C.muted, bg: kpi.overdue > 0 ? '#fee2e2' : '#f1f5f9' },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, border: `1.5px solid ${k.color}33`, borderRadius: 12, padding: '12px 13px' }}>
            <div style={{ fontSize: 18 }}>{k.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: k.color, lineHeight: 1.1, marginTop: 3 }}>{k.value}</div>
            <div style={{ fontSize: 10, color: k.color, fontWeight: 700, marginTop: 3 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'po',        icon: '📋', label: _('purTabPO') },
          { key: 'suppliers', icon: '🏭', label: _('purTabSuppliers') },
          ...(canManage ? [{ key: 'analytics', icon: '📊', label: _('purTabAnalytics') }] : []),
        ].map(t => (
          <button key={t.key} style={{ ...st.btn(activeTab === t.key ? 'primary' : 'default'), padding: '10px 18px', fontSize: 13 }}
            onClick={() => setActiveTab(t.key)}>
            {t.icon} {t.label}
            {t.key === 'po' && kpi.overdue > 0 && activeTab !== 'po' && (
              <span style={{ marginLeft: 5, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 10, padding: '1px 5px' }}>{kpi.overdue}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════ TAB: 採購申請單 ══════════════ */}
      {activeTab === 'po' && (
        <>
          {/* Overdue alert */}
          {kpi.overdue > 0 && (
            <div style={{ marginBottom: 14, background: '#fff7ed', border: '2px solid #fed7aa', borderRadius: 12, padding: '10px 14px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f97316' }}>
                ⏰ {kpi.overdue} 個採購單已超過預計到貨日，請確認進度
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
              <input placeholder={_('purSearchPO')} value={search}
                onChange={e => { setSearch(e.target.value); setPage(1) }}
                style={{ ...st.input, width: 200 }} />
              <select style={{ ...st.input, width: 120 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}>
                <option value="all">全部狀態</option>
                {PO_STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {/* View toggle — desktop only */}
              {!isMobile && (
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2 }}>
                  {[{ v: 'card', icon: '⊞' }, { v: 'table', icon: '☰' }].map(m => (
                    <button key={m.v} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, background: viewMode === m.v ? '#fff' : 'transparent', color: viewMode === m.v ? C.accent : C.muted, boxShadow: viewMode === m.v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}
                      onClick={() => setViewMode(m.v)}>{m.icon}</button>
                  ))}
                </div>
              )}
              <button style={{ ...st.btn('default'), padding: '8px 12px', fontSize: 12 }}
                onClick={() => exportCSV('採購單清單.csv',
                  ['採購單號','供應商','申請人','預計到貨','總金額','狀態','建立時間'],
                  filteredOrders.map(o => [o.po_no, o.supplier_name, o.requested_by||'', o.expected_date||'', o.total_amount||0, statusInfo(o.status).label, formatDate(o.created_at)])
                )}>📥 匯出</button>
              <button style={{ ...st.btn(), padding: '8px 14px', fontSize: 13 }}
                onClick={() => { setShowAddPO(!showAddPO); setEditingPO(null); setPoForm(emptyPO()); setPoItems([emptyItem()]) }}>
                {showAddPO ? _('cancel') : _('purNewPO')}
              </button>
            </div>
          </div>

          {/* Status filter chips */}
          <div style={{ display: 'flex', gap: 5, marginBottom: 14, flexWrap: 'wrap' }}>
            <button style={{ ...st.btn(filterStatus === 'all' ? 'primary' : 'default'), padding: '5px 12px', fontSize: 11 }} onClick={() => { setFilterStatus('all'); setPage(1) }}>
              全部（{orders.length}）
            </button>
            {PO_STATUS.map(s => {
              const cnt = orders.filter(o => o.status === s.key).length
              if (!cnt) return null
              return (
                <button key={s.key}
                  style={{ padding: '5px 12px', fontSize: 11, borderRadius: 20, border: `1.5px solid ${filterStatus === s.key ? s.color : C.border}`, background: filterStatus === s.key ? s.bg : '#fff', color: filterStatus === s.key ? s.color : C.muted, cursor: 'pointer', fontWeight: filterStatus === s.key ? 700 : 400 }}
                  onClick={() => { setFilterStatus(s.key); setPage(1) }}>
                  {s.icon} {s.label}（{cnt}）
                </button>
              )
            })}
          </div>

          {/* PO Form */}
          {showAddPO && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>
                {editingPO ? `${_('purFormEdit')} — ${editingPO.po_no}` : _('purFormNew')}
              </div>
              <POFormBody poForm={poForm} setPoForm={setPoForm} poItems={poItems} setPoItems={setPoItems} poTotal={poTotal} activeSupplierOptions={activeSupplierOptions} suppliers={suppliers} updateItem={updateItem} _={_} />
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '13px' }} onClick={handleSavePO} disabled={saving}>
                  {saving ? _('savingBtn') : editingPO ? '確認更新' : _('purConfirmCreate')}
                </button>
                <button style={{ ...st.btn('default'), padding: '13px 20px' }} onClick={() => { setShowAddPO(false); setEditingPO(null) }}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {/* Card view */}
          {viewMode === 'card' && (
            <>
              {pagedOrders.length === 0 ? (
                <div style={st.card}><EmptyState icon="🛒" text={_('purNoPO')} /></div>
              ) : pagedOrders.map(o => (
                <POCard key={o.id} o={o} canApprove={canApprove} canManage={canManage}
                  onDetail={openDetailPO}
                  onEdit={openEditPO}
                  onStatus={handleUpdateStatus}
                  onCancel={o => { setCancelTarget(o); setCancelReason('') }}
                  onDelete={handleDeletePO}
                  PO_STATUS={PO_STATUS}
                  _={_}
                />
              ))}
              <Pagination page={page} total={totalOrderPages} onChange={setPage} />
            </>
          )}

          {/* Table view */}
          {viewMode === 'table' && (
            <div style={st.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr>{[['採購單號'],['供應商'],['申請人'],['預計到貨'],['總金額'],['狀態'],['操作']].map(([h]) => (
                      <th key={h} style={st.th}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {pagedOrders.length === 0 ? (
                      <tr><td colSpan={7}><EmptyState icon="🛒" text={_('purNoPO')} /></td></tr>
                    ) : pagedOrders.map(o => {
                      const si = statusInfo(o.status)
                      return (
                        <tr key={o.id}>
                          <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent, fontWeight: 700 }}>
                            {o.po_no}
                            {isOverdue(o) && <span style={{ fontSize: 9, marginLeft: 4, color: '#ef4444', fontWeight: 700 }}>⏰逾期</span>}
                          </td>
                          <td style={{ ...st.td, fontWeight: 600, color: C.text }}>{o.supplier_name}</td>
                          <td style={st.td}>{o.requested_by || '-'}</td>
                          <td style={st.td}>{formatDate(o.expected_date) || '-'}</td>
                          <td style={{ ...st.td, fontWeight: 700 }}>NT$ {fmt(o.total_amount)}</td>
                          <td style={st.td}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: si.bg, color: si.color, border: `1px solid ${si.color}44` }}>
                              {si.icon} {si.label}
                            </span>
                          </td>
                          <td style={st.td}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              <button style={{ ...st.btn('default'), padding: '4px 8px', fontSize: 11 }} onClick={() => openDetailPO(o)}>詳情</button>
                              {(o.status === 'draft' || o.status === 'pending') && <button style={{ ...st.btn('warning'), padding: '4px 8px', fontSize: 11 }} onClick={() => openEditPO(o)}>編輯</button>}
                              {o.status === 'draft' && <button style={{ ...st.btn('primary'), padding: '4px 8px', fontSize: 11 }} onClick={() => handleUpdateStatus(o.id, o.po_no, 'pending')}>送審</button>}
                              {o.status === 'pending' && canApprove && <button style={{ ...st.btn('success'), padding: '4px 8px', fontSize: 11 }} onClick={() => handleUpdateStatus(o.id, o.po_no, 'approved')}>核准</button>}
                              {o.status === 'approved' && <button style={{ ...st.btn('primary'), padding: '4px 8px', fontSize: 11 }} onClick={() => handleUpdateStatus(o.id, o.po_no, 'ordered')}>已下單</button>}
                              {o.status === 'ordered' && <button style={{ ...st.btn('success'), padding: '4px 8px', fontSize: 11 }} onClick={() => handleUpdateStatus(o.id, o.po_no, 'received')}>收貨</button>}
                              {!['received','cancelled'].includes(o.status) && canManage && <button style={{ ...st.btn('danger'), padding: '4px 8px', fontSize: 11 }} onClick={() => { setCancelTarget(o); setCancelReason('') }}>取消</button>}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} total={totalOrderPages} onChange={setPage} />
            </div>
          )}
        </>
      )}

      {/* ══════════════ TAB: 供應商管理 ══════════════ */}
      {activeTab === 'suppliers' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
            <input placeholder="🔍 搜尋名稱、代號、聯絡人..." value={supplierSearch}
              onChange={e => { setSupplierSearch(e.target.value); setSupplierPage(1) }}
              style={{ ...st.input, flex: 1, minWidth: 200 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ ...st.btn('default'), padding: '8px 12px', fontSize: 12 }}
                onClick={() => exportCSV('供應商清單.csv',
                  ['供應商名稱','代號','聯絡人','電話','Email','付款條件','地址','狀態'],
                  filteredSuppliers.map(s => [s.name, s.code||'', s.contact||'', s.phone||'', s.email||'', s.payment_terms||'', s.address||'', s.is_active ? _('purSupplierActive') : _('purSupplierInactive')])
                )}>📥 匯出</button>
              <button style={{ ...st.btn(), padding: '8px 14px', fontSize: 13 }}
                onClick={() => { setShowAddSupplier(!showAddSupplier); setEditingSupplier(null); setSupplierForm(emptySupplier()) }}>
                {showAddSupplier ? _('cancel') : _('purNewSupplier')}
              </button>
            </div>
          </div>

          {showAddSupplier && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>
                {editingSupplier ? _('purFormEditSupplier') : _('purFormNewSupplier')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                {[
                  [_('purSupplierName'), 'name',         '公司名稱'],
                  [_('purSupplierCode'), 'code',         'SUP-001'],
                  ['聯絡人',            'contact',      '姓名'],
                  ['電話',              'phone',        '02-xxxx-xxxx'],
                  ['Email',             'email',        'supplier@email.com'],
                  [_('purPaymentTerms'), 'payment_terms', '月結30天'],
                ].map(([label, key, ph]) => (
                  <div key={key}>
                    <label style={st.label}>{label}</label>
                    <input style={st.input} value={supplierForm[key]} placeholder={ph}
                      onChange={e => setSupplierForm(f => ({ ...f, [key]: e.target.value }))} />
                  </div>
                ))}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={st.label}>地址</label>
                  <input style={st.input} value={supplierForm.address} placeholder="供應商地址"
                    onChange={e => setSupplierForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={st.label}>備注</label>
                  <input style={st.input} value={supplierForm.note} placeholder="其他說明"
                    onChange={e => setSupplierForm(f => ({ ...f, note: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '13px' }} onClick={handleSaveSupplier} disabled={saving}>
                  {saving ? _('savingBtn') : editingSupplier ? '確認更新' : '確認新增'}
                </button>
                <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => { setShowAddSupplier(false); setEditingSupplier(null) }}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {/* Supplier cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {pagedSuppliers.length === 0
              ? <div style={st.card}><EmptyState icon="🏭" text={_('purNoSuppliers')} /></div>
              : pagedSuppliers.map(s => (
                <SupplierCard key={s.id} s={s} orders={orders} canManage={canManage}
                  onEdit={s => { setEditingSupplier(s); setSupplierForm({...s}); setShowAddSupplier(true) }}
                  onToggle={async s => { await supabase.from('suppliers').update({ is_active: !s.is_active }).eq('id', s.id); load() }}
                  onDelete={handleDeleteSupplier}
                  onDetail={s => setDetailSupplier(s)}
                  _={_}
                />
              ))
            }
          </div>
          <Pagination page={supplierPage} total={totalSupplierPages} onChange={setSupplierPage} />
        </>
      )}

      {/* ══════════════ TAB: 採購統計 ══════════════ */}
      {activeTab === 'analytics' && canManage && (
        <>
          {/* KPI */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { icon: '💰', label: '本月採購金額', value: `NT$${fmt(kpi.thisMonthSpend)}`, color: '#7c3aed', bg: '#ede9fe', sub: '已收貨訂單合計' },
              { icon: '📋', label: _('purKpiTotalPO'), value: orders.length, color: C.accent, bg: C.accentDim, sub: `進行中 ${orders.filter(o=>!['received','cancelled'].includes(o.status)).length}` },
              { icon: '🏭', label: _('purKpiSuppliers'), value: kpi.activeSuppliers, color: '#0891b2', bg: '#e0f2fe', sub: `共 ${suppliers.length} 家` },
              { icon: '⏰', label: _('purKpiOverdue'), value: kpi.overdue, color: kpi.overdue > 0 ? '#ef4444' : C.muted, bg: kpi.overdue > 0 ? '#fee2e2' : '#f1f5f9', sub: '需跟催' },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, border: `1.5px solid ${k.color}33`, borderRadius: 14, padding: '14px 15px' }}>
                <div style={{ fontSize: 20 }}>{k.icon}</div>
                <div style={{ fontSize: typeof k.value === 'string' && k.value.length > 8 ? 16 : 22, fontWeight: 900, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.value}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: k.color, marginTop: 4 }}>{k.label}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>

            {/* Monthly spend */}
            <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, padding: '16px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 4 }}>💰 每月採購金額</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>近 6 個月已收貨金額（NT$）</div>
              <ColBarChart data={analytics.monthlySpend} color="#7c3aed" height={120} />
            </div>

            {/* Monthly count */}
            <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, padding: '16px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 4 }}>📋 每月採購單數</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>近 6 個月建立的採購單</div>
              <ColBarChart data={analytics.monthlyCount} color={C.accent} height={120} />
            </div>

            {/* Top suppliers */}
            <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>🏆 供應商採購金額排行</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>累計已收貨金額 TOP 6</div>
                </div>
              </div>
              {analytics.topSuppliers.length === 0
                ? <div style={{ color: C.muted, fontSize: 13 }}>尚無已收貨記錄</div>
                : analytics.topSuppliers.map((s, i) => {
                  const maxVal = analytics.topSuppliers[0]?.value || 1
                  return (
                    <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: i < 3 ? '#f59e0b' : C.muted, fontWeight: i < 3 ? 900 : 400, width: 18, textAlign: 'right', flexShrink: 0 }}>#{i+1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ fontSize: 13, color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>{s.label}</span>
                          <span style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed' }}>NT${fmt(s.value)}</span>
                        </div>
                        <div style={{ background: '#f1f5f9', borderRadius: 4, height: 7 }}>
                          <div style={{ width: `${(s.value / maxVal) * 100}%`, height: '100%', background: '#7c3aed', borderRadius: 4, opacity: 0.8 }} />
                        </div>
                      </div>
                    </div>
                  )
                })
              }
            </div>

            {/* Status distribution */}
            <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, padding: '16px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 14 }}>📊 採購單狀態分布</div>
              {analytics.byStatus.length === 0
                ? <div style={{ color: C.muted, fontSize: 13 }}>尚無資料</div>
                : analytics.byStatus.map(s => {
                  const maxCnt = Math.max(...analytics.byStatus.map(x => x.count), 1)
                  return (
                    <div key={s.label} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: C.text }}>{s.icon} {s.label}</span>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 800, color: s.color }}>{s.count}</span>
                          <span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>({Math.round(s.count / orders.length * 100)}%)</span>
                        </div>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                        <div style={{ width: `${(s.count / maxCnt) * 100}%`, height: '100%', background: s.color, borderRadius: 4, opacity: 0.82 }} />
                      </div>
                    </div>
                  )
                })
              }
            </div>

          </div>
        </>
      )}

      {/* ── PO Detail Modal ───────────────────────────────────────────────────── */}
      {detailPO && (
        <Modal title={`${_('purDetailTitle')} — ${detailPO.po_no}`} onClose={() => setDetailPO(null)}>
          <div style={{ marginBottom: 14 }}>
            <StatusPipeline status={detailPO.status} PO_STATUS={PO_STATUS} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            {[
              ['供應商', detailPO.supplier_name],
              ['申請人', detailPO.requested_by || '-'],
              ['預計到貨', formatDate(detailPO.expected_date) || '-'],
              ['核准人', detailPO.approved_by || '-'],
              ['建立時間', formatDate(detailPO.created_at)],
              ['總金額', `NT$ ${fmt(detailPO.total_amount)}`],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 14, color: C.text, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
          {detailPO.note && (
            <div style={{ padding: '9px 12px', background: C.accentDim, borderRadius: 10, fontSize: 13, color: C.sub, marginBottom: 14 }}>
              📝 {detailPO.note}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>採購品項</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['商品','數量','單價','小計'].map(h => <th key={h} style={st.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {detailItems.map((item, i) => (
                  <tr key={i}>
                    <td style={{ ...st.td, fontWeight: 600 }}>{item.item_name || item.product_name || '-'}</td>
                    <td style={st.td}>{item.qty}</td>
                    <td style={st.td}>NT$ {fmt(item.unit_price)}</td>
                    <td style={{ ...st.td, fontWeight: 700, color: C.accent }}>NT$ {fmt(item.total_price)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} style={{ ...st.td, textAlign: 'right', fontWeight: 700 }}>總計</td>
                  <td style={{ ...st.td, fontWeight: 900, color: C.accent, fontSize: 16 }}>NT$ {fmt(detailPO.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* ── Supplier Detail Modal ─────────────────────────────────────────────── */}
      {detailSupplier && (() => {
        const s = detailSupplier
        const myOrders = orders.filter(o => o.supplier_id === s.id || o.supplier_name === s.name)
        const totalSpent = myOrders.filter(o => o.status === 'received').reduce((a,o) => a+(o.total_amount||0), 0)
        return (
          <Modal title={`🏭 ${s.name}`} onClose={() => setDetailSupplier(null)}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                ['代號', s.code || '-'],
                ['聯絡人', s.contact || '-'],
                ['電話', s.phone || '-'],
                ['Email', s.email || '-'],
                ['付款條件', s.payment_terms || '-'],
                ['地址', s.address || '-'],
              ].filter(([,v]) => v && v !== '-').map(([label, value]) => (
                <div key={label} style={{ padding: '9px 12px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {[
                { label: '採購單', value: myOrders.length, color: C.accent },
                { label: '已收貨', value: myOrders.filter(o => o.status === 'received').length, color: '#22c55e' },
                { label: '累計金額', value: `NT$${fmt(totalSpent)}`, color: '#7c3aed' },
              ].map(k => (
                <div key={k.label} style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '10px 12px', textAlign: 'center', border: `1.5px solid ${C.border}` }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: k.color }}>{k.value}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{k.label}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>採購記錄（近 10 筆）</div>
            {myOrders.slice(0, 10).map(o => {
              const si = statusInfo(o.status)
              return (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 6, border: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>{o.po_no}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{formatDate(o.created_at)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>NT$ {fmt(o.total_amount)}</div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: si.color }}>{si.icon} {si.label}</span>
                  </div>
                </div>
              )
            })}
          </Modal>
        )
      })()}

      {/* ── Cancel Modal ─────────────────────────────────────────────────────── */}
      {cancelTarget && (
        <Modal title={`${_('purCancelTitle')} — ${cancelTarget.po_no}`} onClose={() => { setCancelTarget(null); setCancelReason('') }}>
          <div style={{ fontSize: 14, color: C.muted, marginBottom: 14 }}>
            供應商：<strong style={{ color: C.text }}>{cancelTarget.supplier_name}</strong> ·
            金額：<strong style={{ color: C.accent }}>NT$ {fmt(cancelTarget.total_amount)}</strong>
          </div>
          <label style={st.label}>{_('purCancelReason')}</label>
          <textarea style={{ ...st.input, minHeight: 80, marginBottom: 16 }} value={cancelReason}
            placeholder="例：供應商缺貨、需求變更、預算調整..."
            onChange={e => setCancelReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn('danger'), flex: 1, padding: '13px' }} onClick={handleCancelConfirm} disabled={saving}>
              {saving ? _('savingBtn') : _('purConfirmCancel')}
            </button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => { setCancelTarget(null); setCancelReason('') }}>返回</button>
          </div>
        </Modal>
      )}
    </div>
  )
}
